import { useState, useEffect, useCallback, useContext } from 'react';
import { Trash2, RefreshCw, Plus, KeyRound, Copy, Check, ExternalLink } from 'lucide-react';
import { getApiKeys, createApiKey, deleteApiKey } from '@/services/apiKeys.js';
import { updatedSince } from '@/utils.js';
import UserContext from '../../context/users';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { cn } from '@/lib/cn';

function CopyButton({ text }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            toast.success('Copiado!');
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error('Falha ao copiar');
        }
    };

    return (
        <button
            type="button"
            onClick={handleCopy}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-all duration-200"
            style={{
                color: copied ? 'var(--dash-success)' : 'var(--dash-text-muted)',
                background: copied ? 'rgba(52,254,173,0.08)' : 'transparent',
                border: copied ? '1px solid rgba(52,254,173,0.2)' : '1px solid transparent',
            }}
            onMouseEnter={(e) => {
                if (!copied) {
                    e.currentTarget.style.color = 'var(--dash-primary)';
                    e.currentTarget.style.background = 'var(--dash-primary-soft)';
                    e.currentTarget.style.borderColor = 'var(--dash-primary-border)';
                }
            }}
            onMouseLeave={(e) => {
                if (!copied) {
                    e.currentTarget.style.color = 'var(--dash-text-muted)';
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.borderColor = 'transparent';
                }
            }}
        >
            {copied ? <Check size={13} /> : <Copy size={13} />}
        </button>
    );
}

export default function ApiKeysManager() {
    const [apiKeys, setApiKeys] = useState([]);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [currentUser] = useContext(UserContext);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const pageSize = 8;

    const loadApiKeys = useCallback(async () => {
        if (!currentUser?.companyID) return;
        setLoading(true);
        try {
            const { data } = await getApiKeys(1, 100, currentUser.companyID);
            setApiKeys(data ?? []);
        } catch (e) {
            console.error(e);
            toast.error('Falha ao carregar API keys');
        } finally {
            setLoading(false);
        }
    }, [currentUser]);

    useEffect(() => {
        loadApiKeys();
    }, [loadApiKeys]);

    const handleCreate = async () => {
        setGenerating(true);
        try {
            await createApiKey(currentUser.companyID);
            toast.success('API key gerada com sucesso');
            await loadApiKeys();
        } catch (e) {
            console.error(e);
            toast.error('Falha ao gerar API key');
        } finally {
            setGenerating(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await deleteApiKey(deleteTarget.id);
            toast.success('API key removida');
            setDeleteTarget(null);
            await loadApiKeys();
        } catch (e) {
            console.error(e);
            toast.error('Falha ao deletar API key');
        } finally {
            setDeleting(false);
        }
    };

    const totalPages = Math.ceil(apiKeys.length / pageSize);
    const paginatedKeys = apiKeys.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    return (
        <>
            {/* Actions bar */}
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs" style={{ color: 'var(--dash-text-muted)' }}>
                    {apiKeys.length} {apiKeys.length === 1 ? 'chave' : 'chaves'}
                </p>
                <div className="flex gap-2">
                    <Button variant="ghost" size="sm" leftIcon={RefreshCw} onClick={loadApiKeys}>
                        Atualizar
                    </Button>
                    <a href="https://verboo.ai/docs" target="_blank" rel="noreferrer">
                        <Button variant="outline" size="sm" leftIcon={ExternalLink}>
                            Docs
                        </Button>
                    </a>
                    <Button variant="primary" size="sm" leftIcon={Plus} loading={generating} onClick={handleCreate}>
                        Gerar
                    </Button>
                </div>
            </div>

            {/* List */}
            <Card>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="flex flex-col">
                            {[0, 1, 2].map((i) => (
                                <div key={i} className="flex items-center gap-3 border-b px-4 py-3" style={{ borderColor: 'var(--dash-border)' }}>
                                    <div className="h-8 w-8 animate-pulse rounded-xl" style={{ background: 'var(--dash-surface-hover)' }} />
                                    <div className="flex flex-1 flex-col gap-1.5">
                                        <div className="h-3.5 w-64 animate-pulse rounded-md" style={{ background: 'var(--dash-surface-hover)' }} />
                                        <div className="h-2.5 w-24 animate-pulse rounded-md" style={{ background: 'var(--dash-surface-hover)' }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : apiKeys.length === 0 ? (
                        <EmptyState
                            icon={KeyRound}
                            title="Nenhuma API key"
                            description="Gere uma chave para integrar com a API da Verboo."
                            action={
                                <Button variant="primary" size="sm" leftIcon={Plus} loading={generating} onClick={handleCreate}>
                                    Gerar API Key
                                </Button>
                            }
                        />
                    ) : (
                        <div className="flex flex-col">
                            {paginatedKeys.map((item, index) => (
                                <div
                                    key={item.id}
                                    className={cn(
                                        'group flex items-center gap-3 px-4 py-3 transition-colors sm:px-5',
                                        index !== paginatedKeys.length - 1 && 'border-b'
                                    )}
                                    style={{ borderColor: 'var(--dash-border)' }}
                                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--dash-surface-hover)')}
                                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                                >
                                    {/* Icon */}
                                    <div
                                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border"
                                        style={{ background: 'var(--dash-icon-bg)', borderColor: 'var(--dash-border)' }}
                                    >
                                        <KeyRound size={13} style={{ color: 'var(--dash-text-soft)' }} />
                                    </div>

                                    {/* Key content */}
                                    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                                        <div className="flex items-center gap-2">
                                            <code
                                                className="truncate font-mono text-xs"
                                                style={{ color: 'var(--dash-text)' }}
                                            >
                                                {item.data}
                                            </code>
                                            <CopyButton text={item.data} />
                                        </div>
                                        <span className="text-[11px]" style={{ color: 'var(--dash-text-faint)' }}>
                                            Criada há {updatedSince(new Date(item.createdAt))}
                                        </span>
                                    </div>

                                    {/* Delete */}
                                    <button
                                        type="button"
                                        onClick={() => setDeleteTarget(item)}
                                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg opacity-0 transition-all duration-200 group-hover:opacity-100"
                                        style={{ color: 'var(--dash-text-muted)', background: 'transparent' }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.color = '#d61f3f';
                                            e.currentTarget.style.background = 'rgba(254,52,86,0.08)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.color = 'var(--dash-text-muted)';
                                            e.currentTarget.style.background = 'transparent';
                                        }}
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="mt-4 flex items-center justify-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                            key={page}
                            type="button"
                            onClick={() => setCurrentPage(page)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-xs font-medium transition-all duration-200"
                            style={{
                                background: page === currentPage ? 'var(--dash-primary-soft)' : 'transparent',
                                color: page === currentPage ? 'var(--dash-primary)' : 'var(--dash-text-muted)',
                                border: page === currentPage ? '1px solid var(--dash-primary-border)' : '1px solid transparent',
                            }}
                        >
                            {page}
                        </button>
                    ))}
                </div>
            )}

            {/* Delete Confirm */}
            <ConfirmDialog
                open={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDelete}
                title="Remover API Key"
                description="Essa ação vai deletar a API key permanentemente. Ela deixará de funcionar imediatamente."
                confirmLabel="Remover"
                loading={deleting}
            />
        </>
    );
}
